import { env } from '~/env'

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'silent'

const LOG_LEVELS: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
  silent: 100,
}

const getLogLevel = (): LogLevel => {
  const level = env.VITE_LOG_LEVEL || (import.meta.env.MODE === 'production' ? 'silent' : 'debug')
  return level as LogLevel
}

const currentLevel = LOG_LEVELS[getLogLevel()] || LOG_LEVELS.debug

const shouldLog = (level: LogLevel): boolean => LOG_LEVELS[level] >= currentLevel

interface Logger {
  trace: (obj: Record<string, unknown> | string, msg?: string) => void
  debug: (obj: Record<string, unknown> | string, msg?: string) => void
  info: (obj: Record<string, unknown> | string, msg?: string) => void
  warn: (obj: Record<string, unknown> | string, msg?: string) => void
  error: (obj: Record<string, unknown> | string | Error, msg?: string) => void
  fatal: (obj: Record<string, unknown> | string | Error, msg?: string) => void
  child: (bindings: Record<string, unknown>) => Logger
}

const createLoggerInstance = (bindings: Record<string, unknown> = {}): Logger => ({
  trace: (obj, msg) => {
    if (!shouldLog('trace')) return
    if (typeof obj === 'string') console.trace(obj, bindings)
    else console.trace(msg || '', { ...bindings, ...obj })
  },
  debug: (obj, msg) => {
    if (!shouldLog('debug')) return
    if (typeof obj === 'string') console.debug(obj, bindings)
    else console.debug(msg || '', { ...bindings, ...obj })
  },
  info: (obj, msg) => {
    if (!shouldLog('info')) return
    if (typeof obj === 'string') console.info(obj, bindings)
    else console.info(msg || '', { ...bindings, ...obj })
  },
  warn: (obj, msg) => {
    if (!shouldLog('warn')) return
    if (typeof obj === 'string') console.warn(obj, bindings)
    else console.warn(msg || '', { ...bindings, ...obj })
  },
  error: (obj, msg) => {
    if (!shouldLog('error')) return
    if (typeof obj === 'string') console.error(obj, bindings)
    else if (obj instanceof Error) console.error(msg || obj.message, { ...bindings, err: obj })
    else console.error(msg || '', { ...bindings, ...obj })
  },
  fatal: (obj, msg) => {
    if (!shouldLog('fatal')) return
    if (typeof obj === 'string') console.error('[FATAL]', obj, bindings)
    else if (obj instanceof Error)
      console.error('[FATAL]', msg || obj.message, { ...bindings, err: obj })
    else console.error('[FATAL]', msg || '', { ...bindings, ...obj })
  },
  child: (childBindings) => createLoggerInstance({ ...bindings, ...childBindings }),
})

export const log = createLoggerInstance()

/**
 * Create a child logger with additional context
 *
 * @example
 * const authLogger = createLogger({ module: 'auth' })
 * authLogger.info({ userId: '123' }, 'User logged in')
 */
export const createLogger = (bindings: Record<string, unknown>) => {
  return log.child(bindings)
}
